import React from "react";
import { useSearchParams } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import BayarForm, { rupiah } from "@/components/BayarForm";
import BatalNotaForm from "@/components/BatalNotaForm";
import PengembalianPanel from "@/components/PengembalianPanel";
import { StokKeputusanPanel } from "@/components/StokKeputusanPanel";
import { useAuth } from "@/context/AuthContext";
import {
  apiListTagihan,
  apiGetPembayaran,
  apiGetPengaturan,
  apiKoreksiPembayaran,
  apiTindakLanjut,
  formatApiErrorDetail, apiTanpaDp,
} from "@/lib/api";
import { Loader2, RefreshCw, X, Printer, Pencil, Ban, FileText } from "lucide-react";
import { useCetak } from "@/hooks/usePengaturan";
import LinkPublik from "@/components/LinkPublik";
import KirimLink from "@/components/KirimLink";
import PengingatTagihan from "@/components/PengingatTagihan";
import * as W from "@/lib/waktu";

const TABS = [
  { status: "BELUM_BAYAR", label: "Order Pending", hint: "Order yang disimpan tanpa pembayaran." },
  { status: "DP", label: "Pelunasan Piutang", hint: "Order yang sudah DP tetapi belum lunas." },
];
// Owner only: cancelled notas whose payment still has to be settled (F1-D).
const TINDAK_LANJUT = { status: "TINDAK_LANJUT", label: "Perlu tindak lanjut",
  hint: "Nota dibatalkan yang sudah dibayar: catat uang yang dikembalikan atau yang tidak dikembalikan sampai sisa 0." };
const STATUS_STYLE = {
  BELUM_BAYAR: "border-red-500/40 bg-red-500/10 text-red-300",
  DP: "border-amber-500 bg-amber-500 text-slate-950 font-semibold",
  LUNAS: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
};
const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

function fmtTanggal(iso, tz) {
  if (!iso) return "—";
  return W.waktu(iso);
}

function KoreksiForm({ row, rekening, onDone }) {
  const [tunai, setTunai] = React.useState(row.efektif.tunai);
  const [nonTunai, setNonTunai] = React.useState(row.efektif.non_tunai);
  const [rekeningId, setRekeningId] = React.useState("");
  const [alasan, setAlasan] = React.useState("");
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await apiKoreksiPembayaran(row.id, { tunai_baru: parseInt(tunai, 10) || 0, non_tunai_baru: parseInt(nonTunai, 10) || 0,
        rekening_id: rekeningId || null, alasan });
      onDone();
    } catch (e2) { setError(errText(e2)); } finally { setBusy(false); }
  };
  return (
    <form data-testid={`koreksi-form-${row.id}`} onSubmit={submit} className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 items-end rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
      <div><label className="text-[13px] font-semibold text-slate-200">Tunai baru</label><input data-testid="koreksi-tunai" type="number" min="0" value={tunai} onChange={(e) => setTunai(e.target.value)} className={inputCls} /></div>
      <div><label className="text-[13px] font-semibold text-slate-200">Non tunai baru</label><input data-testid="koreksi-non-tunai" type="number" min="0" value={nonTunai} onChange={(e) => setNonTunai(e.target.value)} className={inputCls} /></div>
      <div><label className="text-[13px] font-semibold text-slate-200">Rekening (jika non tunai)</label>
        <select data-testid="koreksi-rekening" value={rekeningId} onChange={(e) => setRekeningId(e.target.value)} className={inputCls}>
          <option value="">{row.efektif.rekening ? `${row.efektif.rekening.bank} (tetap)` : "— pilih —"}</option>
          {(rekening || []).filter((r) => r.is_active).map((r) => <option key={r.id} value={r.id}>{r.bank} {r.nomor}</option>)}
        </select></div>
      <div className="col-span-2 md:col-span-1"><label className="text-[13px] font-semibold text-slate-200">Alasan (wajib)</label><input data-testid="koreksi-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)} required minLength={5} className={inputCls} /></div>
      <button data-testid="koreksi-submit" type="submit" disabled={busy} className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">{busy ? "…" : "Simpan koreksi"}</button>
      {error && <p data-testid="koreksi-error" className="col-span-2 md:col-span-5 text-xs text-red-300">{error}</p>}
      <p className="col-span-2 md:col-span-5 text-[11px] text-slate-500">Koreksi dicatat sebagai entri baru (nilai lama, nilai baru, alasan, siapa, kapan). Pembayaran asal tidak diubah.</p>
    </form>
  );
}

function DetailNota({ notaId, rekening, tz, isOwner, onChanged, onClose, onCancelled }) {
  const cetak = useCetak();
  const [data, setData] = React.useState(null);
  const [batal, setBatal] = React.useState(false);
  const [koreksiId, setKoreksiId] = React.useState(null);
  const [error, setError] = React.useState(null);
  const load = React.useCallback(async () => {
    try { setData(await apiGetPembayaran(notaId)); setError(null); } catch (e) { setError(errText(e)); }
  }, [notaId]);
  React.useEffect(() => { load(); }, [load]);
  const refresh = async () => { setKoreksiId(null); await load(); onChanged(); };
  // Addendum 5 (A5-D): Owner lets an institution order start without DP (reason required)
  const tanpaDp = async () => {
    const alasan = window.prompt("Setujui produksi tanpa DP untuk customer instansi ini. Alasan (minimal 10 karakter):");
    if (alasan === null) return;
    try { await apiTanpaDp(data.order_id, alasan); await refresh(); } catch (e) { setError(errText(e)); }
  };
  if (error) return <div className="text-red-300 text-sm">{error}</div>;
  if (!data) return <div className="p-4 text-slate-400"><Loader2 className="inline animate-spin mr-2" size={16} />Memuat…</div>;
  const r = data.ringkasan;
  const cancelled = data.status_nota === "CANCELLED";
  return (
    <div data-testid="tagihan-detail" className="rounded-xl border border-amber-500/30 bg-slate-900/60 p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <span data-testid="detail-nomor" className="text-xl font-mono text-amber-300">{data.nomor_nota}</span>
        <span data-testid="detail-status" className={`text-xs font-mono px-2 py-1 rounded border ${STATUS_STYLE[r.status_bayar]}`}>{r.status_bayar.replace("_", " ")}</span>
        {cancelled && <span data-testid="detail-dibatalkan" className="text-xs font-mono px-2 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-300">DIBATALKAN</span>}
        <span className="text-sm text-slate-300">Total <b>{rupiah(r.total)}</b> · Dibayar <b data-testid="detail-dibayar">{rupiah(r.dibayar)}</b> · Sisa <b data-testid="detail-sisa">{rupiah(r.sisa)}</b></span>
        <LinkPublik kind="nota" id={notaId} label="Salin link nota" testid="detail-link-nota" />
        <KirimLink notaId={notaId} testid="detail-kirim-link" />
        <a data-testid="detail-invoice" href={`/nota/${notaId}/invoice`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:border-amber-500/50"><FileText size={13} /> Invoice</a>
        {cetak.nota && <a data-testid="detail-cetak" href={`/nota/${notaId}/cetak`} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:border-amber-500/50"><Printer size={13} /> Cetak ulang nota</a>}
        {isOwner && data.is_instansi && data.menunggu_dp && !cancelled && (
          <button data-testid="detail-tanpa-dp" onClick={tanpaDp} className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 px-3 py-1.5 text-xs text-cyan-300 hover:bg-cyan-500/10">Setujui tanpa DP (instansi)</button>)}
        {data.dp_dikecualikan && <span data-testid="detail-dp-dikecualikan" title={data.dp_dikecualikan.alasan} className="text-xs font-mono px-2 py-1 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">TANPA DP (Owner)</span>}
        {isOwner && data.status_nota === "ISSUED" && !batal && (
          <button data-testid="detail-batal" onClick={() => setBatal(true)} className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/10"><Ban size={13} /> Batalkan nota</button>
        )}
        <button data-testid="detail-close" onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
      </div>
      {batal && <BatalNotaForm notaId={notaId} onCancel={() => setBatal(false)}
        onDone={(res) => { setBatal(false); load(); onChanged(); onCancelled(res); }} />}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="riwayat-table">
          <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
            <th className="py-2 pr-3">Tanggal</th><th className="pr-3">Jenis</th><th className="pr-3 text-right">Tunai</th><th className="pr-3 text-right">Non tunai</th>
            <th className="pr-3">Rekening</th><th className="pr-3 text-right">Kembalian</th><th className="pr-3">Kasir</th><th className="pr-3">Keterangan</th><th /></tr></thead>
          <tbody>
            {data.pembayaran.length === 0 && <tr><td colSpan={9} className="py-4 text-center text-slate-500">Belum ada pembayaran.</td></tr>}
            {data.pembayaran.map((p) => (
              <React.Fragment key={p.id}>
                <tr data-testid={`riwayat-${p.id}`} className={`border-b border-slate-800/60 ${p.jenis === "KOREKSI" ? "text-amber-200/90" : ""}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{fmtTanggal(p.tanggal, tz)}</td>
                  <td className="pr-3 text-xs">{p.jenis}{p.dikoreksi ? " (dikoreksi)" : ""}</td>
                  {p.jenis === "PEMBAYARAN" ? (<>
                    <td className="pr-3 text-right font-mono">{rupiah(p.tunai)}{p.dikoreksi && <span className="block text-[10px] text-amber-300">kini {rupiah(p.efektif.tunai)}</span>}</td>
                    <td className="pr-3 text-right font-mono">{rupiah(p.non_tunai)}{p.dikoreksi && <span className="block text-[10px] text-amber-300">kini {rupiah(p.efektif.non_tunai)}</span>}</td>
                    <td className="pr-3 text-xs">{p.rekening ? `${p.rekening.bank} ${p.rekening.nomor}` : "—"}</td>
                    <td className="pr-3 text-right font-mono">{rupiah(p.kembalian)}</td>
                  </>) : (<>
                    <td className="pr-3 text-right font-mono text-xs">{rupiah(p.nilai_lama.tunai)} → {rupiah(p.nilai_baru.tunai)}</td>
                    <td className="pr-3 text-right font-mono text-xs">{rupiah(p.nilai_lama.non_tunai)} → {rupiah(p.nilai_baru.non_tunai)}</td>
                    <td className="pr-3 text-xs">{p.nilai_baru.rekening ? p.nilai_baru.rekening.bank : "—"}</td>
                    <td className="pr-3 text-right font-mono text-xs">—</td>
                  </>)}
                  <td className="pr-3 text-xs">{p.kasir?.nama || p.kasir?.username}</td>
                  <td className="pr-3 text-xs" data-testid={`riwayat-ket-${p.id}`}>{p.jenis === "KOREKSI" ? `Selisih ${p.jumlah > 0 ? "+" : ""}${rupiah(p.jumlah)} · Alasan: ${p.alasan}` : (p.keterangan || "")}</td>
                  <td className="text-right">
                    {isOwner && p.jenis === "PEMBAYARAN" && (
                      <button data-testid={`koreksi-open-${p.id}`} onClick={() => setKoreksiId(koreksiId === p.id ? null : p.id)} className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:text-amber-300"><Pencil size={12} /> Koreksi</button>
                    )}
                  </td>
                </tr>
                {koreksiId === p.id && <tr><td colSpan={9}><KoreksiForm row={p} rekening={rekening} onDone={refresh} /></td></tr>}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {cancelled ? (
        <p data-testid="detail-batal-info" className="text-sm text-red-300">Nota ini dibatalkan; tidak bisa dibayar.{isOwner && r.dibayar > 0 && " Uang yang sudah dibayar diselesaikan di tab \"Perlu tindak lanjut\"."}</p>
      ) : (
        <BayarForm notaId={notaId} ringkasan={r} rekening={rekening} onPaid={() => { load(); onChanged(); }} testid="detail-bayar" />
      )}
    </div>
  );
}

export default function PembayaranPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = React.useState(TABS[0].status);
  const [q, setQ] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [cfg, setCfg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const notaId = params.get("nota");
  const refundId = params.get("pengembalian");
  const isOwner = user?.role === "admin";
  const tabs = isOwner ? [...TABS, TINDAK_LANJUT] : TABS;

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      if (tab === TINDAK_LANJUT.status) {
        const needle = q.trim().toLowerCase();
        const all = await apiTindakLanjut();
        setRows(needle ? all.filter((x) => `${x.nomor_nota} ${x.customer?.nama || ""}`.toLowerCase().includes(needle)) : all);
      } else {
        setRows(await apiListTagihan(tab, q.trim() || undefined));
      }
      setError(null);
    } catch (e) { setError(errText(e)); } finally { setLoading(false); }
  }, [tab, q]);
  const tindakLanjut = tab === TINDAK_LANJUT.status;
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { apiGetPengaturan().then(setCfg).catch(() => {}); }, []);

  return (
    <div data-testid="pembayaran-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Pembayaran</h1>
          <p className="text-sm text-slate-400 mt-1">Tanggal dan kasir dicatat otomatis. Status Belum Bayar / DP / Lunas dihitung dari pembayaran.</p>
        </div>
        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
        {notaId && <DetailNota key={notaId} notaId={notaId} rekening={cfg?.rekening} tz={cfg?.zona_waktu} isOwner={isOwner}
          onChanged={load} onClose={() => setParams({})}
          onCancelled={(res) => { if (res.pembatalan?.pengembalian?.status === "MENUNGGU") { setTab(TINDAK_LANJUT.status); setParams({ pengembalian: notaId }); } }} />}
        <PengingatTagihan />
        {isOwner && refundId && <PengembalianPanel key={refundId} notaId={refundId} rekening={cfg?.rekening} tz={cfg?.zona_waktu}
          onChanged={load} onClose={() => setParams({})} />}
        <div className="flex flex-wrap items-center gap-2">
          {tabs.map((t) => (
            <button key={t.status} data-testid={`tab-${t.status}`} onClick={() => setTab(t.status)}
              className={`rounded-md px-3 py-1.5 text-sm border ${tab === t.status ? "border-amber-500 bg-amber-500 text-slate-950 font-semibold" : "border-slate-700 text-slate-400"}`}>{t.label}</button>
          ))}
          <input data-testid="tagihan-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari faktur / customer"
            className="ml-auto w-full max-w-xs rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm" />
          <button onClick={load} className="text-xs font-mono text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
        </div>
        <p className="text-xs text-slate-500">{tabs.find((t) => t.status === tab).hint}</p>
        {tindakLanjut && isOwner && <StokKeputusanPanel />}
        {tindakLanjut ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tindak-lanjut-table">
            <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
              <th className="px-4 py-3">Nota</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Dibatalkan</th><th className="px-4 py-3">Alasan</th>
              <th className="px-4 py-3 text-right">Dibayar</th><th className="px-4 py-3 text-right">Diselesaikan</th><th className="px-4 py-3 text-right">Sisa</th><th className="px-4 py-3" /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.nota_id} data-testid={`tl-row-${r.nomor_nota}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                  <td className="px-4 py-3 font-mono text-amber-300">{r.nomor_nota}</td>
                  <td className="px-4 py-3">{r.customer?.nama}<span className="block text-[11px] text-slate-500">{r.customer?.kode_customer} {r.customer?.nomor_telepon}</span></td>
                  <td className="px-4 py-3 text-xs font-mono">{fmtTanggal(r.waktu_batal, cfg?.zona_waktu)}</td>
                  <td className="px-4 py-3 text-xs">{r.alasan_batal}</td>
                  <td className="px-4 py-3 text-right font-mono">{rupiah(r.dibayar_saat_batal)}</td>
                  <td className="px-4 py-3 text-right font-mono">{rupiah(r.dikembalikan + r.tidak_dikembalikan)}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-300">{rupiah(r.saldo)}</td>
                  <td className="px-4 py-3 text-right"><button data-testid={`tl-open-${r.nomor_nota}`} onClick={() => { setParams({ pengembalian: r.nota_id }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Selesaikan</button></td>
                </tr>
              ))}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Tidak ada nota batal yang perlu ditindaklanjuti.</td></tr>}
            </tbody>
          </table>
        </div>
        ) : (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tagihan-table">
            <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
              <th className="px-4 py-3">Faktur / Nota</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Tanggal</th><th className="px-4 py-3 text-right">Umur</th>
              <th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right">Dibayar</th><th className="px-4 py-3 text-right">Sisa</th><th className="px-4 py-3" /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.nota_id} data-testid={`tagihan-row-${r.nomor_nota}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                  <td className="px-4 py-3 font-mono text-amber-300">{r.nomor_nota}</td>
                  <td className="px-4 py-3">{r.customer?.nama}<span className="block text-[11px] text-slate-500">{r.customer?.kode_customer} {r.customer?.nomor_telepon}</span></td>
                  <td className="px-4 py-3 text-xs font-mono">{fmtTanggal(r.tanggal, cfg?.zona_waktu)}</td>
                  <td className="px-4 py-3 text-right">{r.umur_hari} hr</td>
                  <td className="px-4 py-3 text-right font-mono">{rupiah(r.total)}</td>
                  <td className="px-4 py-3 text-right font-mono">{rupiah(r.dibayar)}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-300">{rupiah(r.sisa)}</td>
                  <td className="px-4 py-3 text-right"><button data-testid={`tagihan-open-${r.nomor_nota}`} onClick={() => { setParams({ nota: r.nota_id }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-emerald-500/40 hover:text-emerald-300">Bayar / Detail</button></td>
                </tr>
              ))}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Tidak ada tagihan.</td></tr>}
            </tbody>
          </table>
        </div>
        )}
      </main>
    </div>
  );
}
